export const VA_COMPLETION_STATES = new Set(["allvideoscompleted", "novideoplaying", "idle"]);

export interface VaOverlayInputs {
  // Test config enables the assistant, or the current video is a guide video.
  isOverlayEnabled: boolean;
  // The test status says the headset is in the assistant scene. This can be
  // true with no VA message at all when VIRTUAL_ASSISTANCE_VIDEO_UPDATE was
  // lost on a slow network while the headset plays on.
  inVirtualAssistantScene: boolean;
  loading: boolean;
  currentVideoName: string | null;
  videoUrl: string | null;
  loadError: string | null;
  // Raw state from the last VA update message, e.g. "PlayingVideo".
  rawState: string | null;
}

export function isVaCompletionState(rawState: string | null): boolean {
  return rawState ? VA_COMPLETION_STATES.has(rawState.toLowerCase()) : false;
}

// A VA message indicated activity: a named video, or a non-completion state.
export function hasActiveVaSignal(
  currentVideoName: string | null,
  rawState: string | null,
): boolean {
  return Boolean(currentVideoName || (rawState && !isVaCompletionState(rawState)));
}

// Single source of truth for when the assistant stream window renders.
// The window must stay up while the scene says the assistant is active,
// even before any VA message arrived.
export function isVaOverlayHidden(inputs: VaOverlayInputs): boolean {
  const hasActiveVA = hasActiveVaSignal(inputs.currentVideoName, inputs.rawState);
  if (!inputs.isOverlayEnabled && !hasActiveVA && !inputs.inVirtualAssistantScene) {
    return true;
  }
  return (
    !inputs.loading &&
    !inputs.currentVideoName &&
    !inputs.videoUrl &&
    !inputs.loadError &&
    (!inputs.rawState || isVaCompletionState(inputs.rawState)) &&
    !inputs.inVirtualAssistantScene
  );
}

// Which message to show in the window when there is no playable video yet.
export function vaFallbackMessageKey(
  currentVideoName: string | null,
  inVirtualAssistantScene: boolean,
): string {
  if (currentVideoName) {
    return "virtualAssistanceStreamView.videoNotAvailable";
  }
  if (inVirtualAssistantScene) {
    return "virtualAssistanceStreamView.playingOnHeadset";
  }
  return "virtualAssistanceStreamView.noVideoPlaying";
}
